'use client'

import { useState, type ReactNode } from 'react'

import { trackCustomEvent } from '@/hooks/client/use-analytics-engine'
import { EventType } from '@/lib/analytics/events'
import type { BrandLabProduct } from '@/lib/supabase/brand-modules/types'
import { tw } from '@/lib/utils'

import { LabFitPlayer } from './lab-fit-player'
import { LabScoreCard } from './lab-score-card'

export interface LabAudioLabels {
  environments: Record<string, string>
  categories: Record<string, string>
  initialFit: string
  initialFitHint: string
  tunedFit: string
  tunedFitHint: string
  categoryHeadingInitial: string
  categoryHeadingTuned: string
  soundScore: string
  fullReport: string
}

interface LabAudioExplorerProps {
  products: BrandLabProduct[]
  labels: LabAudioLabels
  /** Localized hrefs per product slug (built server-side). */
  reportHrefs: Record<string, string | null>
  /** Server-rendered heading block (kicker/H2/intro/coverage). It lives inside
   *  the explorer's left column so the score card can span the band's full
   *  height and bottom-align with the player cards. */
  header?: ReactNode
}

const chipClass = (active: boolean) =>
  tw(
    'inline-flex min-h-10 cursor-pointer items-center rounded-full border px-4 text-sm font-semibold transition-colors',
    active ? 'border-white bg-white text-navy' : 'border-white/25 text-white/85 hover:border-white/50'
  )

// The interactive half of the lab band: product chips → environment chips →
// the initial/tuned player pair, plus fit-specific category scores for the
// selected product. All copy arrives translated from the server shell; the
// payload was baked (zero fetches here).
export const LabAudioExplorer = ({ products, labels, reportHrefs, header }: LabAudioExplorerProps) => {
  const [productIndex, setProductIndex] = useState(0)
  const product = products[productIndex] ?? products[0]
  const [envId, setEnvId] = useState(product.environments[0]?.id ?? 'quiet')
  const environment = product.environments.find((e) => e.id === envId) ?? product.environments[0]
  const reportHref = reportHrefs[product.productSlug] ?? null

  const selectProduct = (index: number) => {
    setProductIndex(index)
    const next = products[index]
    if (next && !next.environments.some((e) => e.id === envId)) {
      setEnvId(next.environments[0]?.id ?? 'quiet')
    }
    trackCustomEvent('brand-lab--product', EventType.Click, { position: products[index]?.productSlug ?? '' })
  }

  return (
    // items-end bottom-aligns the (taller, photo-topped) score card with the
    // player cards closing the left column.
    <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)] lg:items-end lg:gap-14">
      <div>
        {header && <div className="mb-8">{header}</div>}
        <div className="flex flex-wrap gap-2.5">
          {products.map((p, index) => (
            <button
              key={p.productSlug}
              type="button"
              aria-pressed={index === productIndex}
              data-tracking="ignore"
              onClick={() => selectProduct(index)}
              className={chipClass(index === productIndex)}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {product.environments.map((env) => (
            <button
              key={env.id}
              type="button"
              aria-pressed={env.id === environment?.id}
              data-tracking="ignore"
              onClick={() => setEnvId(env.id)}
              className={tw(
                'inline-flex min-h-9 cursor-pointer items-center rounded-full border px-3.5 text-sm font-medium transition-colors',
                env.id === environment?.id
                  ? 'border-sky-80/60 bg-sky-80/15 text-white'
                  : 'border-white/15 text-white/65 hover:border-white/40'
              )}
            >
              {labels.environments[env.id] ?? env.id}
            </button>
          ))}
        </div>

        {environment && (
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <LabFitPlayer
              kicker={labels.initialFit}
              hint={labels.initialFitHint}
              url={environment.initialUrl}
              score={environment.initialScore}
              group="brand-lab"
              tone="initial"
            />
            <LabFitPlayer
              kicker={labels.tunedFit}
              hint={labels.tunedFitHint}
              url={environment.tunedUrl}
              score={environment.tunedScore}
              group="brand-lab"
              tone="tuned"
            />
          </div>
        )}
      </div>

      <LabScoreCard key={product.productSlug} product={product} labels={labels} reportHref={reportHref} />
    </div>
  )
}
